<template>
  <div class="d-flex">
    <field-radio-hidden
      v-bind="propsAndAttrs"
      :model-value="modelValue"
      :val="val"
      @update:model-value="emitInput"
    >
      <template v-slot="{ selected }">
        <q-radio
          v-bind="propsAndAttrs"
          class="tm-field-radio"
          :class="radioButtonClasses"
          :model-value="modelValue"
          :val="val"
          :disable="isDisabled"
          :tabindex="radioTabindex"
          @update:model-value="emitInput"
        >
          <template
            v-if="$slots.default"
            v-slot
          >
            <slot v-bind="{ selected }" />
          </template>
        </q-radio>
      </template>
    </field-radio-hidden>
  </div>
</template>

<script setup lang="ts" generic="T extends string | number | boolean">
import type { PropType } from '@/composition/vue/compositionApi'
import { computed } from '@/composition/vue/compositionApi'
import { fieldProps } from '@/components/forms/fieldBase'
import { useField } from '@/composition/forms/useField'
import { fieldEmits } from '@/services/forms/types'
import FieldRadioHidden from '@/components/forms/fields/FieldRadioHidden.vue'
import type { FieldRadioBorderColor, FieldRadioSize } from '@/components/forms/fields/types'

const defaultFieldRadioSize: FieldRadioSize = 'md'

const props = defineProps({
  ...fieldProps,
  modelValue: {
    type: [String, Number, Boolean] as PropType<T>,
    required: true,
  },
  alignTop: {
    type: Boolean,
    default: false,
  },
  labelWidthFull: {
    type: Boolean,
    default: false,
  },
  fieldClass: {
    type: String,
  },
  val: {
    type: [String, Number, Boolean] as PropType<T>,
    required: true,
  },
  size: {
    type: String as PropType<FieldRadioSize>,
  },
  borderColor: {
    type: String as PropType<FieldRadioBorderColor>,
  },
})

const emit = defineEmits({
  ...fieldEmits<T>(),
})

const { propsAndAttrs, emitInput } = useField<T>(props, emit, ['class', 'tabIndex'])

// Quasar's prop is lowercase `tabindex`; passing the camelCase `tabIndex` through fallthrough attrs strips it.
const radioTabindex = computed(() => (props.isDisabled ? -1 : (props.tabIndex ?? 0)))

const size = computed(() => props.size || defaultFieldRadioSize)

const radioButtonClasses = computed<Record<string, boolean>>(() => {
  const borderColor = props.borderColor ?? 'default'
  return {
    'tm-field-radio--align-top': props.alignTop,
    'tm-field-radio--label-width-full': props.labelWidthFull,
    [`tm-field-radio--size-${size.value}`]: true,
    [`tm-field-radio--border-color-${borderColor}`]: true,
    [props.fieldClass!]: !!props.fieldClass,
  }
})

defineExpose({
  focus: () => {}, // error fix from initiator TmFormLine after click
})
</script>

<style lang="scss" scoped>
// radio style
.tm-field-radio {
  &:focus-visible :deep(.q-radio__inner) {
    outline-color: $blue400;
  }

  &--align-top :deep(.q-radio__inner) {
    margin-top: 2px;
  }

  &--border-color-default :deep(.q-radio__inner) {
    border: 1px solid $border-button;
  }

  &--border-color-orange :deep(.q-radio__inner) {
    border: 1px solid $orange;
  }

  :deep(.q-radio__inner) {
    background-color: $white;
    border-radius: 50%;
    transition:
      all $transition-300ms-ease-in-out,
      outline 0s;
    box-shadow: 0 2px 4px rgba($black, 0.05);

    // for :focus-visible
    outline-offset: 2px;
    outline: 2px solid transparent;

    // hide ripple animation
    &::before {
      content: none !important;
    }
    &:hover {
      background: $shade;
    }
    &.q-radio__inner--truthy {
      border-color: $primary;
      &:hover {
        border-color: $blue600;
        .q-radio__bg {
          background-color: $blue600;
        }
      }
    }

    // hide ripple animation
    &::before {
      content: none !important;
    }

    // icon imitation
    .q-radio__bg {
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      border-radius: 50%;
      background-color: $primary;
      opacity: 0;
      transition: opacity $transition-200ms-ease-in-out;
      path {
        display: none;
      }
    }

    // selected state
    &.q-radio__inner--truthy .q-radio__bg {
      opacity: 1;
    }
  }

  &--size-md {
    :deep(.q-radio__inner) {
      font-size: $md-font;

      .q-radio__bg {
        width: 8px;
        height: 8px;
      }
    }
    :deep(.q-radio__label) {
      margin-left: 8px;
    }
  }

  &--size-lg-x {
    :deep(.q-radio__inner) {
      font-size: $lg-x-font;

      .q-radio__bg {
        width: 12px;
        height: 12px;
      }
    }
    :deep(.q-radio__label) {
      margin-left: 12px;
    }
  }
}

.q-radio.tm-field-radio {
  display: flex;
}

// label style
.tm-field-radio {
  :deep(.q-radio__label) {
    font-size: $font-size-root;
    font-weight: var(--tm-field-radio-label-font-weight, #{$semi-bold-font});
    line-height: 20px;
  }

  &--label-width-full :deep(.q-radio__label) {
    width: 100%;
  }
}

// disabled state
.tm-field-radio {
  &.disabled,
  &[disabled] {
    opacity: 1 !important;
    cursor: initial !important;
    :deep(.q-radio__inner) {
      border-color: $gray1;
      background: $lift;
      &.q-radio__inner--truthy {
        border-color: $blue200 !important;
      }
      .q-radio__bg {
        background: $blue200 !important;
        cursor: initial !important;
      }
    }
    :deep(.q-radio__label) {
      color: $light;
      cursor: initial !important;
    }
  }
}

// Align radio and label by flex-start
.tm-field-radio--align-top {
  align-items: flex-start;
}
</style>
